//codex-- Keep the toolbar redesign scoped to pages in this folder.
import PageToolbar from './PageToolbar';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { NavLink, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import DateFormat from '../components/DateFormat';
import CurrencyFormat from '../components/CurrencyFormat';
import '../styles/PayrollPage.css';
import HeadingCp from '../components/HeadingCp';
import MonthSelectorCp from '../components/MonthSelectorCp';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const PayrollPage = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [fetchItems, setFetchItems] = useState([]);
  const [filterItems, setFilterItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [weekDays, setWeekDays] = useState([]);
  const [weekDays2, setWeekDays2] = useState([]);
  const [week, setWeek] = useState('active_week');
  const [payperiodMonth, setPayPeriodMonth] = useState(null);
  const [payperiod, setPayPeriod] = useState(null);
  const [holiday, setHoliday] = useState(null);
  const [onCallTechs, setOnCallTechs] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);

  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = String(dateString).split('-').map(Number);
    return new Date(year, month - 1, day);
  };  

  // const tableWrapperRef = useRef(null);
  // const pdfTableRef = useRef(null);

  const formatPdfDate = (dateString) => {
    if (!dateString) return '';
    const date = parseLocalDate(dateString);
    if (!date || Number.isNaN(date.getTime())) return dateString;

    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
    });
  };

  const getTotalHours = (object) => {
    return (
      (parseFloat(object.RH) || 0) +
      (parseFloat(object.VH) || 0) +
      (parseFloat(object.HH) || 0)
    );
  };

  const getDayCellTextForPdf = (object, date) => {
    const isHoliday = Array.isArray(holiday) && holiday.includes(date);
    const isOnCall = isOnCallForDate(date, object.id);
    const dateRequest = getEmployeeDateRequest(object.days, date);
    const vacationRequest = getEmployeeVacation(object.vacation_days, date);

    let baseText = 'NS';

    if (isHoliday) {
      baseText = 'Holiday';
    } else if (dateRequest !== 'NS') {
      baseText = dateRequest;
    } else if (vacationRequest !== 'NS') {
      baseText = vacationRequest;
    }

    if (isOnCall) {
      baseText = baseText === 'NS' ? 'On-Call' : `${baseText}\nOn-Call`;
    }

    return baseText;
  };

  const buildPayrollPdfColumns = () => {
    const staticColumns = [
      { header: 'Employee Name', dataKey: 'employee_name' },
      { header: 'TH', dataKey: 'TH' },
      { header: 'OT', dataKey: 'OT' },
      { header: 'RH', dataKey: 'RH' },
      { header: 'VH', dataKey: 'VH' },
      { header: 'HH', dataKey: 'HH' },
    ];

    const dateColumns = payrollDisplayDays.map((date) => ({
      header: formatPdfDate(date),
      dataKey: `day_${date}`,
    }));

    const endColumns = [
      { header: 'Expense | Notes', dataKey: 'expense_notes' },
    ];

    return [...staticColumns, ...dateColumns, ...endColumns];
  };

  const buildPayrollPdfRows = () => {
    return filterItems.map((object) => {
      const fieldExpense = object.expense?.expense_amount
        ? `${object.expense.expense_amount}`
        : '';

      const officeExpense = object.officeExpense
        ? `${object.officeExpense}`
        : '';

      const notes = object.notes ? `${object.notes}` : '';

      let expenseNotes = '';

      if (object.o_f_type === 'Field') {
        expenseNotes = [fieldExpense, notes].filter(Boolean).join('\n');
      } else {
        expenseNotes = [officeExpense, notes].filter(Boolean).join('\n');
      }

      const row = {
        employee_name: `${object.employee_name || ''}\n(${object.id}) ${object.department || ''}`,
        TH: `${getTotalHours(object)}`,
        OT: `${parseFloat(object.OT) || 0}`,
        RH: `${parseFloat(object.RH) || 0}`,
        VH: `${parseFloat(object.VH) || 0}`,
        HH: `${parseFloat(object.HH) || 0}`,
        expense_notes: expenseNotes,
      };

      payrollDisplayDays.forEach((date) => {
        row[`day_${date}`] = getDayCellTextForPdf(object, date);
      });

      return row;
    });
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
  };

  const formatDateHeader = (dateString) => {
    const date = parseLocalDate(dateString);
    if (!date || Number.isNaN(date.getTime())) return dateString;

    const monthDay = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
    });
    const weekday = date.toLocaleDateString('en-US', {
      weekday: 'short',
    });

    return (
      <div className="leading-tight text-center">
        <div>{monthDay}</div>
        <div className="text-[10px] text-gray-500">{weekday}</div>
      </div>
    );
  };

  const getDateRequestColor = (date_request) => {
    if (!date_request) return '';

    if (date_request.toLowerCase().includes('no pay')) {
      return 'bg-red-400';
    }

    switch (date_request) {
      case 'in':
        return 'bg-green-300';
      case 'Vacation Day':
        return 'bg-blue-300';
      case 'Half Vacation Day':
        return 'bg-orange-300';
      case 'Sick Day':
        return 'bg-indigo-300';
      case 'Excused Pay':
        return 'bg-red-300';
      case 'Partial Hour':
        return 'bg-red-300';
      default:
        return '';
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPdf = async () => {
    if (!filterItems.length) {
      toast.info('No payroll data to export.', { position: 'top-center' });
      return;
    }

    try {
      setExportingPdf(true);

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      const columns = buildPayrollPdfColumns();
      const rows = buildPayrollPdfRows();

      const generatedOn = new Date().toLocaleString('en-US');
      const reportTitle = 'Payroll Report';
      const reportSubTitle = payperiod
        ? `Pay Period: ${payperiod}`
        : 'Pay Period: Active Week';

      const marginLeft = 6;
      const marginRight = 6;
      const marginTop = 24;
      const marginBottom = 12;

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const availableWidth = pageWidth - marginLeft - marginRight;

      const employeeWidth = 30;
      const metricWidth = 10;
      const expenseWidth = 25;

      const fixedWidth = employeeWidth + (metricWidth * 5) + expenseWidth;
      const remainingWidth = availableWidth - fixedWidth;

      const dayWidth =
        payrollDisplayDays.length > 0
          ? Math.max(7, remainingWidth / payrollDisplayDays.length)
          : 10;

      const dynamicColumnStyles = {
        employee_name: { cellWidth: employeeWidth, halign: 'left' },
        TH: { cellWidth: metricWidth, halign: 'center' },
        OT: { cellWidth: metricWidth, halign: 'center' },
        RH: { cellWidth: metricWidth, halign: 'center' },
        VH: { cellWidth: metricWidth, halign: 'center' },
        HH: { cellWidth: metricWidth, halign: 'center' },
        expense_notes: { cellWidth: expenseWidth, halign: 'left' },
      };

      payrollDisplayDays.forEach((date) => {
        dynamicColumnStyles[`day_${date}`] = {
          cellWidth: dayWidth,
          halign: 'center',
        };
      });

      autoTable(doc, {
        columns,
        body: rows,
        startY: marginTop,
        margin: {
          top: marginTop,
          right: marginRight,
          bottom: marginBottom,
          left: marginLeft,
        },

        showHead: 'everyPage',
        pageBreak: 'auto',
        rowPageBreak: 'auto',

        theme: 'grid',
        tableWidth: availableWidth,

        styles: {
          fontSize: 5.8,
          cellPadding: 0.5,
          overflow: 'linebreak',
          valign: 'middle',
          lineColor: [180, 180, 180],
          lineWidth: 0.1,
          textColor: [20, 20, 20],
        },

        headStyles: {
          fillColor: [55, 65, 81],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
        },

        bodyStyles: {
          valign: 'middle',
        },

        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },

        columnStyles: dynamicColumnStyles,

        didParseCell: (data) => {
          if (data.section !== 'body') return;

          const columnKey = data.column.dataKey;
          const raw = String(data.cell.raw || '');

          if (typeof columnKey === 'string' && columnKey.startsWith('day_')) {
            if (raw.includes('Holiday')) {
              data.cell.styles.fillColor = [253, 224, 71];
            } else if (raw.toLowerCase().includes('no pay')) {
              data.cell.styles.fillColor = [248, 113, 113];
            } else if (raw.toLowerCase().includes('in')) {
              data.cell.styles.fillColor = [106, 247, 106];
            } else if (raw.includes('Vacation Day')) {
              data.cell.styles.fillColor = [147, 197, 253];
            } else if (raw.includes('Half Vacation Day')) {
              data.cell.styles.fillColor = [253, 186, 116];
            } else if (raw.includes('Sick Day')) {
              data.cell.styles.fillColor = [165, 180, 252];
            } else if (
              raw.includes('Excused Pay') ||
              raw.includes('Partial Hour')
            ) {
              data.cell.styles.fillColor = [252, 165, 165];
            }

            if (raw.includes('On-Call')) {
              data.cell.styles.textColor = [109, 40, 217];
              data.cell.styles.fontStyle = 'bold';
            }

            data.cell.styles.halign = 'center';
          }

          if (columnKey === 'TH') {
            const value = parseFloat(raw) || 0;
            if (value > 80) {
              data.cell.styles.fillColor = [239, 68, 68];
              data.cell.styles.textColor = [255, 255, 255];
              data.cell.styles.fontStyle = 'bold';
            } else if (value < 80) {
              data.cell.styles.fillColor = [254, 215, 170];
            } else {
              data.cell.styles.fillColor = [191, 219, 254];
            }
            data.cell.styles.halign = 'center';
          }

          if (columnKey === 'OT') {
            const value = parseFloat(raw) || 0;
            if (value > 0) {
              data.cell.styles.fillColor = [216, 180, 254];
            }
            data.cell.styles.halign = 'center';
          }

          if (['RH', 'VH', 'HH'].includes(columnKey)) {
            data.cell.styles.halign = 'center';
          }

          if (columnKey === 'expense_notes' && raw.trim()) {
            data.cell.styles.textColor = [107, 33, 168];
          }
        },

        willDrawPage: () => {
          doc.setFontSize(14);
          doc.setTextColor(17, 24, 39);
          doc.text(reportTitle, marginLeft, 10);

          doc.setFontSize(9);
          doc.setTextColor(75, 85, 99);
          doc.text(reportSubTitle, marginLeft, 15);

          if (selectedMonth) {
            doc.text(`Selected Month: ${selectedMonth}`, marginLeft, 19);
          }

          doc.text(
            `Generated: ${generatedOn}`,
            pageWidth - marginRight,
            10,
            { align: 'right' }
          );
        },

        didDrawPage: (data) => {
          doc.setFontSize(8);
          doc.setTextColor(100);
          doc.text(
            `Page ${data.pageNumber}`,
            pageWidth - marginRight,
            pageHeight - 4,
            { align: 'right' }
          );
        },
      });

      doc.save(`payroll-report-${payperiod || 'report'}.pdf`);

      toast.success('PDF exported successfully.', {
        position: 'top-center',
      });
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF.', {
        position: 'top-center',
      });
    } finally {
      setExportingPdf(false);
    }
  };

  const handleSearchClick = async () => {
    if (!searchTerm) {
      toast.info('Search bar empty', { position: 'top-center' });
      return;
    }

    setLoading(true);

    const bodyData = {
      status: 'searchDelivery',
      data: { search: searchTerm },
    };

    try {
      const response = await axios.post(`${apiUrl}/wip_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data && Array.isArray(response.data)) {
        setFetchItems(response.data);
        setFilterItems(response.data);
      } else {
        setFetchItems([]);
        setFilterItems([]);
        toast.error(`Not found: ${searchTerm}`, { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error searching deliveries', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (value === '') {
      setFilterItems(fetchItems);
      return;
    }

    const filtered = fetchItems.filter(
      (object) =>
        (object.description &&
          object.description.toLowerCase().includes(value.toLowerCase())) ||
        (object.job_number &&
          object.job_number.toLowerCase().includes(value.toLowerCase())) ||
        (object.job_site &&
          object.job_site.toLowerCase().includes(value.toLowerCase()))
    );

    setFilterItems(filtered);
  };

  const handleWeek = (e, date) => {
    e.preventDefault();
    setWeek(date);
  };

  const isOnCallForDate = (date, employeeId) => {
    if (onCallTechs === 'not on call' || !Array.isArray(onCallTechs)) {
      return false;
    }

    const currentDate = new Date(date).setHours(0, 0, 0, 0);

    return onCallTechs.some((tech) => {
      const techStart = new Date(tech.start_date).setHours(0, 0, 0, 0);
      const techEnd = new Date(tech.end_date).setHours(0, 0, 0, 0);

      return (
        Number(tech.employee_id) === Number(employeeId) &&
        currentDate >= techStart &&
        currentDate <= techEnd
      );
    });
  };

  useEffect(() => {
    setLoading(true);
    fetchEmployeePayroll(selectedMonth, week, apiUrl, navigate);
  }, [selectedMonth, week, apiUrl, navigate]);

  const fetchEmployeePayroll = async (
    selectedMonth,
    week,
    apiUrl,
    navigate
  ) => {
    try {
      const response = await axios.get(
        `${apiUrl}/payroll_crud.php?payroll=${week}&&selectedMonth=${selectedMonth}`,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          },
        }
      );
      const payroll = Array.isArray(response.data.employees)
        ? response.data.employees
        : [];

      if (payroll.length === 0) {
        toast.error('No payroll report found!', { position: 'top-center' });
      }

      setFilterItems(payroll || []);
      setFetchItems(payroll || []);
      setWeekDays(response.data.pay_period?.week1 || []);
      setWeekDays2(response.data.pay_period?.week2 || []);
      setPayPeriodMonth(response.data.pay_periods_month || []);
      setPayPeriod(response.data.date || null);
      setHoliday(response.data.holiday || []);
      setOnCallTechs(response.data.onCall || []);
      
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error('Session expired. Please login again.', {
          position: 'top-center',
        });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: 'top-center',
        });
      } else {
        console.error('Payroll fetch error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // ⚠️ TEMP PATCH:
// filterWeekDays intentionally excludes Friday + Saturday to match legacy payroll behavior.
// This is NOT correct calendar logic.
// Do NOT refactor until vacation/date normalization bug is resolved.

const getPayrollDisplayDays = (days) => {
  const sorted = [...new Set(days)]
    .filter(Boolean)
    .sort((a, b) => parseLocalDate(a) - parseLocalDate(b));

  return sorted.filter((day) => {
    const d = parseLocalDate(day);
    const dow = d.getDay();
    return dow !== 0 && dow !== 6; // remove Sunday and Saturday
  });
};

  const getEmployeeDateRequest = (days, date) => {
    if (!Array.isArray(days)) return 'NS';

    const day = days.find((d) => d.date_out === date);

    if (!day) {
      return 'NS';
    }

    if (day.date_request === 'Partial Hour') {
      return `${day.date_request} ${day.partial_hours || ''}`.trim();
    }

    if (day.date_request === 'No Pay') {
      return `${day.date_request}${day.notes ? ` - ${day.notes}` : ''}`;
    }

    return day.date_request;
  };

  const getEmployeeVacation = (vacationDays, date) => {
    if (!Array.isArray(vacationDays)) return 'NS';
    return vacationDays.includes(date) ? 'Vacation Day' : 'NS';
  };


  // const filteredWeekDays = filterWeekDays([...weekDays, ...weekDays2]);
  // const filteredWeekDaysHeader = filterWeekDaysHeader([...weekDays, ...weekDays2]);
const payrollDisplayDays = getPayrollDisplayDays([...weekDays, ...weekDays2]);
  
  return (
    <div className="w-full max-w-[85vw] mx-auto px-2 sm:px-4 lg:px-6 py-6 payroll-print-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <HeadingCp label="Payroll Report" />
        </div>

        <>{/*codex-- Match the updated toolbar: grouped actions, labeled controls, and lighter original colors. */}
          <PageToolbar title="Payroll tools" actions={<>
            <button onClick={handleExportPdf} disabled={loading || exportingPdf || filterItems.length === 0} className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${loading || exportingPdf || filterItems.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}>
              {exportingPdf ? 'Exporting PDF...' : 'Export PDF'}
            </button>
            <button onClick={handlePrint} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700">Print</button>
          </>} />
      </>
      </div>

      <>{/*codex-- Match the updated toolbar: grouped actions, labeled controls, and lighter original colors. */}
        <PageToolbar title="Payroll period" actions={<>
          {payperiodMonth ? payperiodMonth.map(pay_period_week => <button key={pay_period_week} className={`px-4 py-1 text-white rounded-md hover:bg-blue-600 ${pay_period_week === payperiod ? 'bg-green-500' : 'bg-blue-500'}`} onClick={e => handleWeek(e, pay_period_week)}>
            <DateFormat date={pay_period_week} />
          </button>) : <LoadingSpinner children="Loading..." />}
        </>}>
          <MonthSelectorCp onMonthChange={handleMonthChange} />
        </PageToolbar>
      </>

      <div className="mt-6">
        {loading ? (
          <LoadingSpinner children="Loading..." />
        ) : (
          <div className="shadow ring-1 ring-black ring-opacity-5 w-full mx-auto overflow-x-hidden bg-white payroll-print-wrapper">
           <table className="w-full table-fixed border-separate border-spacing-0 text-[11px] payroll-print-table">
              <thead className="bg-white">
                <tr>
                  <th className="sticky left-0 z-40 w-[150px] border-b border-r border-gray-300 bg-white px-2 py-2 text-left font-semibold text-gray-900 payroll-print-sticky-reset">
                    Employee Name
                  </th>

                  <th className="w-[60px] max-w-[60px] border-b border-r border-gray-300 bg-white px-1 py-2 text-center font-semibold text-gray-900 whitespace-nowrap">
                    TH
                  </th>

                  <th className="w-[60px] max-w-[60px] border-b border-r border-gray-300 bg-white px-1 py-2 text-center font-semibold text-gray-900 whitespace-nowrap">
                    OT
                  </th>

                  <th className="w-[60px] max-w-[60px] border-b border-r border-gray-300 bg-white px-1 py-2 text-center font-semibold text-gray-900 whitespace-nowrap">
                    RH
                  </th>

                  <th className="w-[60px] max-w-[60px] border-b border-r border-gray-300 bg-white px-1 py-2 text-center font-semibold text-gray-900 whitespace-nowrap">
                    VH
                  </th>

                  <th className="w-[60px] max-w-[60px] border-b border-r border-gray-300 bg-white px-1 py-2 text-center font-semibold text-gray-900 whitespace-nowrap">
                    HH
                  </th>

                  {payrollDisplayDays.map((date) => (
                    <th
                      key={date}
                      className="w-[58px] border-b border-r border-gray-300 bg-white px-1 py-2 font-semibold text-gray-900"
                    >
                      {formatDateHeader(date)}
                    </th>
                  ))}

                  <th className="w-[140px] border-b border-gray-300 bg-white px-2 py-2 text-left font-semibold text-gray-900">
                    Expense | Notes
                  </th>
                </tr>
              </thead>

              <tbody>
                {filterItems.map((object, objectIdx) => {
                  const totalHours =
                    (parseFloat(object.RH) || 0) +
                    (parseFloat(object.VH) || 0) +
                    (parseFloat(object.HH) || 0);

                  return (
                    <tr
                      key={object.id}
                      className={`${
                        objectIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      } text-xs`}
                    >
                      <td className="sticky left-0 z-30 w-[150px] border-b border-r border-gray-300 bg-inherit px-2 py-2 align-top payroll-print-sticky-reset break-words">
                        <span className="text-blue-800 font-medium">
                          <NavLink to={`/profile/${object.id}`}>
                            {object.employee_name}
                          </NavLink>
                        </span>
                        <br />
                        <span className="text-gray-500 text-[11px]">
                          ({object.id}) {object.department}
                        </span>
                      </td>

                      <td
                        className={`w-[60px] max-w-[60px] border-b border-r border-gray-300 px-1 py-2 text-center whitespace-nowrap ${
                          (parseFloat(object.RH) || 0) + (parseFloat(object.VH) || 0) > 80
                            ? 'bg-red-500 text-white'
                            : (parseFloat(object.RH) || 0) + (parseFloat(object.VH) || 0) < 80
                            ? 'bg-[#fed7aa]'
                            : 'bg-[#bfdbfe]'
                        }`}
                      >
                        TH {totalHours}
                      </td>

                      <td
                        className={`w-[60px] max-w-[60px] border-b border-r border-gray-300 px-1 py-2 text-center whitespace-nowrap ${
                          parseFloat(object.OT) > 0 ? 'bg-purple-300' : ''
                        }`}
                      >
                        OT {parseFloat(object.OT) || 0}
                      </td>

                      <td className="w-[60px] max-w-[60px] border-b border-r border-gray-300 px-1 py-2 text-center whitespace-nowrap">
                        RH {parseFloat(object.RH) || 0}
                      </td>

                      <td className="w-[60px] max-w-[60px] border-b border-r border-gray-300 px-1 py-2 text-center whitespace-nowrap">
                        VH {parseFloat(object.VH) || 0}
                      </td>

                      <td className="w-[60px] max-w-[60px] border-b border-r border-gray-300 px-1 py-2 text-center whitespace-nowrap">
                        HH {parseFloat(object.HH) || 0}
                      </td>

                      {payrollDisplayDays.map((date) => {
                        
                        const dateRequest = getEmployeeDateRequest(object.days, date);
                        const vacationRequest = getEmployeeVacation(
                          object.vacation_days,
                          date
                        );
                        const isHoliday = Array.isArray(holiday) && holiday.includes(date);
                        const isOnCall = isOnCallForDate(date, object.id);

                        return (
                          <td
                            key={date}
                            className={`border-b border-r border-gray-300 px-1 py-2 text-center align-top w-[58px] break-words
                              ${isHoliday ? 'bg-yellow-300' : ''}
                              ${
                                dateRequest !== 'NS'
                                  ? getDateRequestColor(dateRequest)
                                  : getDateRequestColor(vacationRequest)
                              }
                              ${isOnCall ? 'text-violet-700 font-semibold' : ''}
                            `}
                          >
                            {isHoliday ? (
                              'Holiday'
                            ) : (
                              <>
                                <div className="leading-tight break-words">
                                  {dateRequest !== 'NS' ? dateRequest : vacationRequest}
                                </div>
                                {isOnCall && (
                                  <div className="mt-1 text-[10px] font-semibold">
                                    On-Call
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                        );
                      })}

                      <td className="border-b border-gray-300 px-2 py-2 align-top w-[140px] break-words">
                        {object.o_f_type === 'Field' ? (
                          object.expense?.expense_amount ? (
                            <span className="text-purple-700">
                              <CurrencyFormat value={object.expense.expense_amount} />
                            </span>
                          ) : null
                        ) : object.officeExpense ? (
                          <div className="leading-tight">
                            <div className="text-purple-700">
                              <CurrencyFormat value={object.officeExpense} />
                            </div>
                            {object.notes && (
                              <div className="text-gray-600 mt-1 break-words">
                                {object.notes}
                              </div>
                            )}
                          </div>
                        ) : (
                          object.notes || ''
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollPage;